import * as THREE from 'three';

export function createParticleEffects({ scene, world, player, CACTUS_BLOCK_ID }) {
const debrisParticleGeo = new THREE.BoxGeometry(1, 1, 1);
const debrisParticlePool = [];
const debrisParticleMaterialCache = new Map();
const activeDebrisParticles = [];
const MAX_DEBRIS_PARTICLES = 260;
const DEBRIS_GRAVITY = 22.5;
const DEBRIS_DRAG = 2.35;
const cactusPulseShellGeo = new THREE.BoxGeometry(1, 1, 1);
const cactusPulseShellPool = [];
const activeCactusPulseShells = [];
const MAX_CACTUS_PULSE_SHELLS = 10;
const CACTUS_PULSE_BASE_WIDTH = 1.1;
const CACTUS_PULSE_DURATION = 0.34;

function parseHexColor(value, fallback = 0x94a0ab) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.min(0xffffff, Math.floor(value)));
  }
  if (typeof value !== 'string' || value.length <= 0) return fallback;
  const normalized = value.startsWith('#') ? value.slice(1) : value;
  const parsed = Number.parseInt(normalized, 16);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.min(0xffffff, parsed));
}

function getDebrisColorByBlockId(blockId = null) {
  const def = Number.isFinite(Number(blockId)) ? world.blockData?.[Number(blockId)] : null;
  if (!def) return 0x95a0aa;
  return parseHexColor(def.color, 0x95a0aa);
}

function getDebrisParticleMaterial(color = 0x95a0aa) {
  const key = Math.max(0, Math.min(0xffffff, Math.floor(Number(color) || 0x95a0aa)));
  let mat = debrisParticleMaterialCache.get(key);
  if (!mat) {
    mat = new THREE.MeshStandardMaterial({
      color: key,
      roughness: 0.86,
      metalness: 0.02
    });
    debrisParticleMaterialCache.set(key, mat);
  }
  return mat;
}

function acquireDebrisParticle(color = 0x95a0aa) {
  const mesh = debrisParticlePool.pop() || new THREE.Mesh(debrisParticleGeo, getDebrisParticleMaterial(color));
  mesh.material = getDebrisParticleMaterial(color);
  mesh.visible = true;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

function recycleDebrisParticle(entry) {
  if (!entry?.mesh) return;
  scene.remove(entry.mesh);
  entry.mesh.visible = false;
  if (debrisParticlePool.length < MAX_DEBRIS_PARTICLES) {
    debrisParticlePool.push(entry.mesh);
  }
}

function createCactusPulseShell() {
  const group = new THREE.Group();
  group.visible = false;

  const aura = new THREE.Mesh(
    cactusPulseShellGeo,
    new THREE.MeshBasicMaterial({
      color: 0x3d1f57,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    })
  );
  aura.scale.set(1.2, 1.06, 1.2);
  aura.renderOrder = 39;
  group.add(aura);

  const fill = new THREE.Mesh(
    cactusPulseShellGeo,
    new THREE.MeshBasicMaterial({
      color: 0x58b84b,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    })
  );
  fill.renderOrder = 40;
  group.add(fill);

  const wire = new THREE.Mesh(
    cactusPulseShellGeo,
    new THREE.MeshBasicMaterial({
      color: 0x24132e,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      wireframe: true
    })
  );
  wire.renderOrder = 41;
  group.add(wire);

  return {
    group,
    aura,
    fill,
    wire,
    key: '',
    ttl: CACTUS_PULSE_DURATION,
    life: 0,
    rootX: 0,
    rootY: 0,
    rootZ: 0,
    height: 1,
    strength: 0.18,
    baseScaleX: CACTUS_PULSE_BASE_WIDTH,
    baseScaleY: 1.06,
    baseScaleZ: CACTUS_PULSE_BASE_WIDTH,
    pivotY: 0,
    phase: Math.random() * Math.PI * 2,
    spinDirection: Math.random() < 0.5 ? -1 : 1
  };
}

function acquireCactusPulseShell() {
  const entry = cactusPulseShellPool.pop() || createCactusPulseShell();
  entry.group.visible = true;
  scene.add(entry.group);
  return entry;
}

function recycleCactusPulseShell(entry) {
  if (!entry?.group) return;
  scene.remove(entry.group);
  entry.group.visible = false;
  entry.group.scale.set(1, 1, 1);
  entry.group.rotation.set(0, 0, 0);
  entry.aura.material.opacity = 0;
  entry.fill.material.opacity = 0;
  entry.wire.material.opacity = 0;
  entry.key = '';
  entry.strength = 0.18;
  if (cactusPulseShellPool.length < MAX_CACTUS_PULSE_SHELLS) {
    cactusPulseShellPool.push(entry);
  }
}

function setCactusPulseShellTransform(entry) {
  if (!entry?.group) return;
  const safeHeight = Math.max(1, Math.floor(Number(entry.height) || 1));
  entry.baseScaleX = CACTUS_PULSE_BASE_WIDTH;
  entry.baseScaleY = safeHeight + 0.08;
  entry.baseScaleZ = CACTUS_PULSE_BASE_WIDTH;
  entry.group.position.set(
    Number(entry.rootX) || 0,
    (Number(entry.rootY) || 0) + ((safeHeight - 1) * 0.5),
    Number(entry.rootZ) || 0
  );
  entry.pivotY = entry.group.position.y;
  entry.group.scale.set(entry.baseScaleX, entry.baseScaleY, entry.baseScaleZ);
  entry.aura.scale.set(1.18, 1.05, 1.18);
  entry.fill.scale.set(1, 1, 1);
  entry.wire.scale.set(1.05, 1.02, 1.05);
}

function spawnCactusAbsorbShellPulse(rootX, rootY, rootZ, height = 1) {
  if (![rootX, rootY, rootZ].every((value) => Number.isFinite(Number(value)))) return;

  const safeHeight = Math.max(1, Math.floor(Number(height) || 1));
  const key = `${Math.round(Number(rootX))}:${Math.round(Number(rootY))}:${Math.round(Number(rootZ))}`;
  let entry = activeCactusPulseShells.find((pulse) => pulse.key === key) || null;
  const wasActive = !!entry;

  if (!entry) {
    if (activeCactusPulseShells.length >= MAX_CACTUS_PULSE_SHELLS) {
      recycleCactusPulseShell(activeCactusPulseShells.shift());
    }
    entry = acquireCactusPulseShell();
    activeCactusPulseShells.push(entry);
  }

  entry.key = key;
  entry.rootX = Number(rootX) || 0;
  entry.rootY = Number(rootY) || 0;
  entry.rootZ = Number(rootZ) || 0;
  entry.height = safeHeight;
  entry.ttl = CACTUS_PULSE_DURATION;
  entry.life = entry.ttl;
  entry.strength = wasActive
    ? Math.min(0.34, Math.max(0.18, Number(entry.strength) || 0.18) + 0.03)
    : 0.22;
  setCactusPulseShellTransform(entry);
}

function spawnBlockDebrisBurst(
  x,
  y,
  z,
  {
    blockId = null,
    count = 8,
    spread = 0.4,
    speed = 2.6,
    upward = 2.4,
    lifeMin = 0.22,
    lifeMax = 0.56
  } = {}
) {
  const available = Math.max(0, MAX_DEBRIS_PARTICLES - activeDebrisParticles.length);
  const spawnCount = Math.min(available, Math.max(1, Math.floor(count)));
  if (spawnCount <= 0) return;

  const color = getDebrisColorByBlockId(blockId);
  const minLife = Math.max(0.08, Math.min(Number(lifeMin) || 0.22, Number(lifeMax) || 0.56));
  const maxLife = Math.max(minLife + 0.02, Number(lifeMax) || 0.56);

  for (let i = 0; i < spawnCount; i++) {
    const mesh = acquireDebrisParticle(color);
    const baseScale = 0.028 + Math.random() * 0.068;
    mesh.scale.setScalar(baseScale);
    mesh.position.set(
      x + (Math.random() * 2 - 1) * spread,
      y + Math.random() * 0.15,
      z + (Math.random() * 2 - 1) * spread
    );
    mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
    scene.add(mesh);

    const yaw = Math.random() * Math.PI * 2;
    const radial = speed * (0.45 + Math.random() * 0.78);
    const ttl = minLife + Math.random() * (maxLife - minLife);
    activeDebrisParticles.push({
      mesh,
      vx: Math.cos(yaw) * radial,
      vy: upward * (0.48 + Math.random() * 0.9),
      vz: Math.sin(yaw) * radial,
      spinX: (Math.random() * 2 - 1) * 9.2,
      spinY: (Math.random() * 2 - 1) * 9.2,
      spinZ: (Math.random() * 2 - 1) * 9.2,
      baseScale,
      ttl,
      life: ttl,
      bounces: 0
    });
  }
}

function spawnCactusConsumeParticles(
  sourceX,
  sourceY,
  sourceZ,
  {
    targetX = sourceX,
    targetY = sourceY,
    targetZ = sourceZ,
    count = 6
  } = {}
) {
  const available = Math.max(0, MAX_DEBRIS_PARTICLES - activeDebrisParticles.length);
  const spawnCount = Math.min(available, Math.max(1, Math.floor(count)));
  if (spawnCount <= 0) return;

  const color = getDebrisColorByBlockId(CACTUS_BLOCK_ID);
  for (let i = 0; i < spawnCount; i++) {
    const mesh = acquireDebrisParticle(color);
    const baseScale = 0.012 + Math.random() * 0.022;
    const startX = sourceX + (Math.random() * 2 - 1) * 0.1;
    const startY = sourceY + Math.random() * 0.12;
    const startZ = sourceZ + (Math.random() * 2 - 1) * 0.1;
    const jitteredTargetX = targetX + (Math.random() * 2 - 1) * 0.08;
    const jitteredTargetY = targetY + (Math.random() * 2 - 1) * 0.16;
    const jitteredTargetZ = targetZ + (Math.random() * 2 - 1) * 0.08;
    const dx = jitteredTargetX - startX;
    const dy = jitteredTargetY - startY;
    const dz = jitteredTargetZ - startZ;
    const distance = Math.max(0.12, Math.sqrt((dx * dx) + (dy * dy) + (dz * dz)));
    const initialSpeed = 1.35 + Math.random() * 1.25;
    const ttl = 0.18 + Math.random() * 0.18;
    mesh.scale.setScalar(baseScale);
    mesh.position.set(startX, startY, startZ);
    mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
    scene.add(mesh);

    activeDebrisParticles.push({
      mesh,
      vx: (dx / distance) * initialSpeed,
      vy: (dy / distance) * initialSpeed + 0.16,
      vz: (dz / distance) * initialSpeed,
      spinX: (Math.random() * 2 - 1) * 10.5,
      spinY: (Math.random() * 2 - 1) * 10.5,
      spinZ: (Math.random() * 2 - 1) * 10.5,
      baseScale,
      ttl,
      life: ttl,
      bounces: 0,
      gravityScale: 0.14,
      dragScale: 0.62,
      disableCollision: true,
      targetX: jitteredTargetX,
      targetY: jitteredTargetY,
      targetZ: jitteredTargetZ,
      pullStrength: 15 + Math.random() * 7
    });
  }
}

function spawnCactusConsumePulse(targetX, targetY, targetZ, count = 5) {
  const available = Math.max(0, MAX_DEBRIS_PARTICLES - activeDebrisParticles.length);
  const spawnCount = Math.min(available, Math.max(3, Math.floor(count)));
  if (spawnCount <= 0) return;

  const color = 0xb8f58a;
  for (let i = 0; i < spawnCount; i++) {
    const mesh = acquireDebrisParticle(color);
    const angle = (i / spawnCount) * Math.PI * 2;
    const radius = 0.12 + Math.random() * 0.06;
    const startX = targetX + Math.cos(angle) * radius;
    const startY = targetY + (Math.random() * 0.08 - 0.04);
    const startZ = targetZ + Math.sin(angle) * radius;
    const dx = targetX - startX;
    const dy = targetY - startY;
    const dz = targetZ - startZ;
    const distance = Math.max(0.08, Math.sqrt((dx * dx) + (dy * dy) + (dz * dz)));
    const ttl = 0.1 + Math.random() * 0.08;
    const baseScale = 0.008 + Math.random() * 0.012;
    mesh.scale.setScalar(baseScale);
    mesh.position.set(startX, startY, startZ);
    mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
    scene.add(mesh);

    activeDebrisParticles.push({
      mesh,
      vx: (dx / distance) * (0.65 + Math.random() * 0.35),
      vy: (dy / distance) * (0.65 + Math.random() * 0.35),
      vz: (dz / distance) * (0.65 + Math.random() * 0.35),
      spinX: (Math.random() * 2 - 1) * 6.5,
      spinY: (Math.random() * 2 - 1) * 6.5,
      spinZ: (Math.random() * 2 - 1) * 6.5,
      baseScale,
      ttl,
      life: ttl,
      bounces: 0,
      gravityScale: 0.02,
      dragScale: 0.45,
      disableCollision: true,
      targetX,
      targetY,
      targetZ,
      pullStrength: 12 + Math.random() * 5
    });
  }
}

function updateDebrisParticles(delta = 0.016) {
  const safeDelta = Math.max(0, Number(delta) || 0);
  if (safeDelta <= 0 || activeDebrisParticles.length <= 0) return;

  for (let i = activeDebrisParticles.length - 1; i >= 0; i--) {
    const p = activeDebrisParticles[i];
    p.life -= safeDelta;
    if (p.life <= 0) {
      recycleDebrisParticle(p);
      activeDebrisParticles.splice(i, 1);
      continue;
    }

    const gravityScale = Number.isFinite(Number(p.gravityScale))
      ? Math.max(0, Number(p.gravityScale))
      : 1;
    const dragScale = Number.isFinite(Number(p.dragScale))
      ? Math.max(0, Number(p.dragScale))
      : 1;
    const dragFactor = Math.max(0, 1 - DEBRIS_DRAG * dragScale * safeDelta);
    const targetX = Number(p.targetX);
    const targetY = Number(p.targetY);
    const targetZ = Number(p.targetZ);
    const pullStrength = Math.max(0, Number(p.pullStrength) || 0);

    if (Number.isFinite(targetX) && Number.isFinite(targetY) && Number.isFinite(targetZ) && pullStrength > 0) {
      const dx = targetX - p.mesh.position.x;
      const dy = targetY - p.mesh.position.y;
      const dz = targetZ - p.mesh.position.z;
      const distSq = (dx * dx) + (dy * dy) + (dz * dz);
      if (distSq > 0.0001) {
        const invDist = 1 / Math.sqrt(distSq);
        const pullDelta = pullStrength * safeDelta;
        p.vx += dx * invDist * pullDelta;
        p.vy += dy * invDist * pullDelta;
        p.vz += dz * invDist * pullDelta;
      }
    }

    p.vy -= DEBRIS_GRAVITY * gravityScale * safeDelta;
    p.vx *= dragFactor;
    p.vz *= dragFactor;

    p.mesh.position.x += p.vx * safeDelta;
    p.mesh.position.y += p.vy * safeDelta;
    p.mesh.position.z += p.vz * safeDelta;

    const probeY = p.mesh.position.y - (p.baseScale * 0.65 + 0.05);
    if (!p.disableCollision && p.vy < 0 && world.isSolidAt(p.mesh.position.x, probeY, p.mesh.position.z)) {
      const groundY = Math.floor(probeY + 0.5);
      p.mesh.position.y = groundY + 0.5 + (p.baseScale * 0.58);
      p.vy *= -0.3;
      p.vx *= 0.62;
      p.vz *= 0.62;
      p.bounces += 1;
      if (p.bounces >= 2 || Math.abs(p.vy) < 0.4) p.vy = 0;
    }

    const lifeRatio = THREE.MathUtils.clamp(p.life / p.ttl, 0, 1);
    const scaled = p.baseScale * (0.42 + lifeRatio * 0.88);
    p.mesh.scale.setScalar(scaled);
    p.mesh.rotation.x += p.spinX * safeDelta;
    p.mesh.rotation.y += p.spinY * safeDelta;
    p.mesh.rotation.z += p.spinZ * safeDelta;
  }
}

function updateCactusPulseShells(delta = 0.016) {
  const safeDelta = Math.max(0, Number(delta) || 0);
  if (safeDelta <= 0 || activeCactusPulseShells.length <= 0) return;

  for (let i = activeCactusPulseShells.length - 1; i >= 0; i--) {
    const pulse = activeCactusPulseShells[i];
    pulse.life -= safeDelta;
    if (pulse.life <= 0) {
      recycleCactusPulseShell(pulse);
      activeCactusPulseShells.splice(i, 1);
      continue;
    }

    const progress = 1 - (pulse.life / Math.max(0.001, pulse.ttl));
    const wave = Math.sin(progress * Math.PI);
    const shimmer = Math.sin(progress * Math.PI * 6 + pulse.phase);
    const settle = progress > 0.58 ? (progress - 0.58) / 0.42 : 0;
    const flutter = shimmer * 0.035 * (1 - progress * 0.45);
    const radialScalarX = Math.max(0.92, 1 + wave * pulse.strength + flutter - settle * 0.1);
    const radialScalarZ = Math.max(0.92, 1 + wave * (pulse.strength * 0.92) - flutter - settle * 0.1);
    const heightScalar = Math.max(0.94, 1 + wave * 0.08 - settle * 0.04);
    const opacityFade = Math.max(0, Math.min(1, wave * (1 - progress * 0.08)));
    const auraScale = 1.16 + wave * 0.28 + Math.max(0, shimmer) * 0.08;

    pulse.group.scale.set(
      pulse.baseScaleX * radialScalarX,
      pulse.baseScaleY * heightScalar,
      pulse.baseScaleZ * radialScalarZ
    );
    pulse.group.position.y = pulse.pivotY + wave * 0.035;
    pulse.group.rotation.y = progress * pulse.spinDirection * 1.25;
    pulse.group.rotation.x = shimmer * 0.025;
    pulse.group.rotation.z = -shimmer * 0.025;
    pulse.aura.scale.set(auraScale, 1.04 + wave * 0.06, auraScale);
    pulse.fill.material.opacity = opacityFade * 0.22;
    pulse.aura.material.opacity = opacityFade * (0.1 + Math.max(0, shimmer) * 0.08);
    pulse.wire.material.opacity = opacityFade * (0.26 + Math.max(0, shimmer) * 0.08);
  }
}

function spawnLandingDebris(fallDistance = 0) {
  const distance = Math.max(0, Number(fallDistance) || 0);
  if (distance < 4.2) return;
  const pos = player.getPosition();
  const impactY = Math.floor(player.getFeetY() - 0.5) + 0.52;
  const blockId = world.getBlockIdAt(pos.x, impactY - 0.6, pos.z);
  const intensity = THREE.MathUtils.clamp((distance - 3.2) / 8.6, 0, 1);

  spawnBlockDebrisBurst(pos.x, impactY, pos.z, {
    blockId,
    count: Math.floor(10 + intensity * 24),
    spread: 0.42 + intensity * 0.5,
    speed: 2.1 + intensity * 2.4,
    upward: 2.0 + intensity * 2.8,
    lifeMin: 0.26,
    lifeMax: 0.62
  });
}

  function spawnMobHitBurst(x, y, z) {
    const available = Math.max(0, MAX_DEBRIS_PARTICLES - activeDebrisParticles.length);
    const count = Math.min(available, 5);
    if (count <= 0) return;

    for (let i = 0; i < count; i++) {
      // red with slight variation
      const r = 0.7 + Math.random() * 0.3;
      const g = 0.1 + Math.random() * 0.15;
      const b = 0.08 + Math.random() * 0.1;
      const colorHex = (Math.floor(r * 255) << 16) | (Math.floor(g * 255) << 8) | Math.floor(b * 255);
      const mesh = acquireDebrisParticle(colorHex);
      const baseScale = 0.018 + Math.random() * 0.04;
      mesh.scale.setScalar(baseScale);
      mesh.position.set(
        x + (Math.random() * 2 - 1) * 0.25,
        y + Math.random() * 0.2,
        z + (Math.random() * 2 - 1) * 0.25
      );
      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      scene.add(mesh);

      const yaw = Math.random() * Math.PI * 2;
      const radial = 3.0 * (0.4 + Math.random() * 0.8);
      const ttl = 0.15 + Math.random() * 0.2;
      activeDebrisParticles.push({
        mesh,
        vx: Math.cos(yaw) * radial,
        vy: 3.5 * (0.5 + Math.random() * 0.8),
        vz: Math.sin(yaw) * radial,
        spinX: (Math.random() * 2 - 1) * 12,
        spinY: (Math.random() * 2 - 1) * 12,
        spinZ: (Math.random() * 2 - 1) * 12,
        baseScale,
        ttl,
        life: ttl,
        bounces: 0
      });
    }
  }

  return {
    spawnBlockDebrisBurst,
    spawnMobHitBurst,
    spawnCactusConsumeParticles,
    spawnCactusConsumePulse,
    spawnCactusAbsorbShellPulse,
    spawnLandingDebris,
    updateDebrisParticles,
    updateCactusPulseShells
  };
}
